import React, { useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from '../../i18n';

interface AddCertificationDialogProps {
  open: boolean;
  saving: boolean;
  name: string;
  expiry: string;
  document: File | null;
  onNameChange: (name: string) => void;
  onExpiryChange: (expiry: string) => void;
  onDocumentChange: (document: File | null) => void;
  onClose: () => void;
  onAdd: () => void;
}

/**
 * "Add certification" dialog for the User Profile page (STD-014 split out of
 * UserProfilePage.tsx). Form state is owned by the parent so the success-path
 * reset (clear name/expiry/document + close) behaves exactly as before; this
 * component is presentational over those props plus the local doc-input ref.
 */
export const AddCertificationDialog: React.FC<AddCertificationDialogProps> = ({
  open,
  saving,
  name,
  expiry,
  document,
  onNameChange,
  onExpiryChange,
  onDocumentChange,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const certDocInputRef = useRef<HTMLInputElement>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('profile.addCertification')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t('profile.certificationName')}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          sx={{ mt: 2, mb: 2 }}
        />
        <TextField
          fullWidth
          type="date"
          label={t('profile.expiryDate')}
          value={expiry}
          onChange={(e) => onExpiryChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <Box>
          <Button
            variant="outlined"
            onClick={() => certDocInputRef.current?.click()}
            startIcon={<DescriptionIcon />}
          >
            {document ? document.name : t('profile.uploadDocument')}
          </Button>
          <input
            type="file"
            ref={certDocInputRef}
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={(e) => onDocumentChange(e.target.files?.[0] || null)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={onAdd} disabled={!name.trim() || saving}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
